/**
 * Shared re-authentication primitives for sensitive account actions.
 *
 * Firebase guards its sensitive client-side calls — `verifyBeforeUpdateEmail`,
 * `user.delete()`, `updatePassword` — with `auth/requires-recent-login`. The
 * failure is identical across all of them, so the handling belongs in one place
 * rather than being re-derived per feature.
 *
 * ONE USER-FACING STATE. This follows `useDeleteAccount`'s existing rule (see
 * its docblock): every stale-session failure, whatever its origin, collapses
 * onto a single "we need to confirm it's you" state. The user never sees a raw
 * Firebase code, and no caller gets to invent a second vocabulary for it.
 *
 * WHY PASSWORD, AND ONLY PASSWORD. `reauthenticateWithPassword` covers the one
 * provider that can actually leave an account in the state these features care
 * about: email+password signup, which is the only path that produces a signed-in
 * user with `emailVerified === false`. Federated providers hand back an already
 * verified address, so re-prompting them here would be a form nobody can fill.
 * Callers must therefore check `hasPasswordProvider` BEFORE showing a password
 * prompt and route the rest to sign-out/sign-in — see `useChangeEmail`, which
 * reports `no-password` rather than rendering a field the user cannot satisfy.
 */

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  type User,
} from 'firebase/auth';

/** Firebase's provider id for email + password credentials. */
export const PASSWORD_PROVIDER_ID = 'password';

export type ReauthError =
  /** The password did not match. The one error the user can fix by retyping. */
  | 'wrong-password'
  /** Firebase's per-account attempt throttle. */
  | 'too-many'
  /** No password on this account, so no password prompt can succeed. */
  | 'no-password'
  /** Anything else. */
  | 'unknown';

function errorCodeOf(err: unknown): string {
  if (typeof err !== 'object' || err === null) return '';
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

/**
 * True when Firebase refused because the session is too old.
 *
 * Substring rather than equality: the code arrives as `auth/requires-recent-login`
 * from the SDK but as a bare `requires-recent-login` through some callable and
 * emulator paths, and both mean the same thing.
 */
export function isRequiresRecentLogin(err: unknown): boolean {
  return errorCodeOf(err).includes('requires-recent-login');
}

/**
 * True when this account can actually be re-authenticated with a password.
 *
 * Read from `providerData`, not from `isAnonymous` or the current sign-in
 * method: an anonymous guest upgraded via `linkWithCredential` keeps its uid and
 * gains a password provider, and that user is exactly who this flow serves.
 */
export function hasPasswordProvider(user: User | null | undefined): boolean {
  if (!user) return false;
  return (user.providerData ?? []).some((p) => p?.providerId === PASSWORD_PROVIDER_ID);
}

/**
 * Maps a failed re-authentication onto one UI state.
 *
 * `auth/invalid-credential` is folded into 'wrong-password' because
 * `enableImprovedEmailPrivacy` is on (see v4Auth.ts): Firebase deliberately
 * stops distinguishing "no such user" from "wrong password" so the response
 * cannot be used to enumerate accounts. For a re-auth the user is already
 * signed in, so the only honest reading left is "that password was wrong".
 */
export function mapReauthError(err: unknown): ReauthError {
  const code = errorCodeOf(err);
  if (
    code.includes('wrong-password') ||
    code.includes('invalid-credential') ||
    code.includes('invalid-login-credentials')
  ) {
    return 'wrong-password';
  }
  if (code.includes('too-many-requests')) return 'too-many';
  if (code.includes('missing-password') || code.includes('operation-not-allowed')) {
    return 'no-password';
  }
  return 'unknown';
}

/**
 * Re-authenticate with the account's own password, refreshing the session so
 * the sensitive call that just failed can be retried.
 *
 * Throws on failure — callers map with `mapReauthError`. Rejecting rather than
 * returning a flag is deliberate: a swallowed failure here would let a caller
 * proceed as if the user had proven themselves.
 */
export async function reauthenticateWithPassword(user: User, password: string): Promise<void> {
  if (!user.email) throw Object.assign(new Error('no email'), { code: 'auth/no-password' });
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
}
